import { Box, Icon, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TableColumn } from '../../../../../Component/Model/Table/Table';
import { DefaultCell } from '../../../../../Component/Common/Components/DataTable/DefaultCell';
import colors from '../../../../../assets/theme/base/colors';
import { DeviationsDto } from '../../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { raceDisplayNames, genderDisplayNames, movementTypeDisplayNames, demographicGroupLabel } from '../../Shared/deviationsCommon';
import {
    BadgeTone,
    priorityBadgeLabel,
    priorityBadgeTone,
    deviationBadgeTone,
    formatMovementDate,
} from './recruitmentPromotionMovementsHelper';

interface MovementRow {
    individualId: number;
    name: string;
    movementTypeLabel: string;
    movementDate: string;
    raceLabel: string;
    genderLabel: string;
    priority: string;
    deviation: string;
    record: DeviationsDto.RecruitmentPromotionMovementDto;
}

interface MovementsTableData {
    columns: TableColumn[];
    rows: MovementRow[];
}

const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.25,
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
} as const;

function paletteFor(tone: BadgeTone) {
    return colors.deviationBadge[tone];
}

function PriorityBadge({ movement, onClick }: { movement: DeviationsDto.RecruitmentPromotionMovementDto; onClick: (movement: DeviationsDto.RecruitmentPromotionMovementDto) => void }) {
    const palette = paletteFor(priorityBadgeTone(movement));
    return (
        <Box
            component="button"
            onClick={() => onClick(movement)}
            title={`${priorityBadgeLabel(movement)} - ${demographicGroupLabel(movement.raceId, movement.genderId)}`}
            sx={{
                ...pillSx,
                cursor: 'pointer',
                backgroundColor: palette.bg,
                color: palette.text,
                border: `1px solid ${palette.border}`,
            }}
        >
            {priorityBadgeLabel(movement)}
            <InfoOutlinedIcon sx={{ width: 13, height: 13, ml: 0.5, opacity: 0.6 }} />
        </Box>
    );
}

function DeviationBadge({ value }: { value: string }) {
    const palette = paletteFor(deviationBadgeTone(value));
    return (
        <Box component="span" sx={{ ...pillSx, backgroundColor: palette.bg, color: palette.text }}>
            {value || 'N/A'}
        </Box>
    );
}

export function RecruitmentPromotionMovementsDataTableColumns(
    movements: DeviationsDto.RecruitmentPromotionMovementDto[],
    onPriorityClick: (movement: DeviationsDto.RecruitmentPromotionMovementDto) => void,
    onPriorityInfoClick: () => void,
    onDeviationInfoClick: () => void,
    onJustificationClick: (movement: DeviationsDto.RecruitmentPromotionMovementDto) => void,
): MovementsTableData {
    const columns: TableColumn[] = [
        { Header: 'Name', accessor: 'name', id: 'EmployeeName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Movement', accessor: 'movementTypeLabel', id: 'MovementTypeId', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Movement Date', accessor: 'movementDate', id: 'MovementDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Race', accessor: 'raceLabel', id: 'Race', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Gender', accessor: 'genderLabel', id: 'Gender', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Priority at Date of Movement', accessor: 'priority', id: 'PriorityAtDateOfMovement', infoIconEnabled: true, infoTooltip: 'How Priority at Date of Movement is calculated', onInfoClick: onPriorityInfoClick, Cell: ({ row }: any) => <PriorityBadge movement={row.original.record} onClick={onPriorityClick} /> },
        { Header: 'Deviation Relative to EE Plan', accessor: 'deviation', id: 'DeviationRelativeToPlan', infoIconEnabled: true, infoTooltip: 'What Deviation Relative to EE Plan means', onInfoClick: onDeviationInfoClick, Cell: ({ value }: any) => <DeviationBadge value={value} /> },
        {
            Header: 'Justification',
            accessor: 'justification',
            id: 'Justification',
            disableSortBy: true,
            Cell: ({ row }: any) => {
                const movement: DeviationsDto.RecruitmentPromotionMovementDto = row.original.record;
                const hasJustification = movement.individualJustificationId != null;
                return (
                    <IconButton
                        size='small'
                        onClick={() => onJustificationClick(movement)}
                        title={hasJustification ? 'Edit Justification' : 'Add Justification'}
                    >
                        <Icon sx={{ color: hasJustification ? colors.primary.main : colors.ui.secondaryText, fontSize: '1.25rem' }}>
                            {hasJustification ? 'edit' : 'add'}
                        </Icon>
                    </IconButton>
                );
            },
        },
    ];

    const rows: MovementRow[] = movements.map(record => ({
        individualId: record.individualId,
        name: record.fullName,
        movementTypeLabel: movementTypeDisplayNames[record.movementTypeId] ?? 'Movement',
        movementDate: formatMovementDate(record.movementDate),
        raceLabel: raceDisplayNames[record.raceId] ?? 'Unspecified',
        genderLabel: genderDisplayNames[record.genderId] ?? '',
        priority: priorityBadgeLabel(record),
        deviation: record.deviationRelativeToPlan,
        justification: null,
        record,
    }));

    return { columns, rows };
}
